import * as React from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { ApplicationStatusTag } from "../../../molecules/status-tag";
import { PaymentsStatusTag } from "../../../molecules/payments-status-tag";
import { formatDatetime } from "../../../../utils/date-utils";
import { Loader } from "../../common/loading";
import {
  ApplicationEntity,
  ApplyStatus,
} from "../../../../application/domain/application/application-entity";
import { SmartApplyR } from "../../../pages/smart-apply/route";
import { useModal } from "../../../../utils/custom-hooks";
import { StatusFilterDropDown } from "../../../organisms/smart-apply/application-list/status-filter-dropdown";
import {
  Link,
  SecondaryButton,
  Table,
  TableBody,
  TableCheckBoxTd,
  TableCheckBoxTh,
  TableHeader,
  TableRow,
  Td,
  Th,
} from "@graffer-inc/console-design-kit";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import { BulkApplicationSelectDropdown } from "./bulk-application-select-dropdown";
import { ApplicantSchemaKeys } from "@graffer-inc/cloud-gov-core";
import "./application-list.scss";

export type SelectableOption = {
  selectedApplicationIds: Array<string>;
  selectedProcedure?: ProcedureEntity;
  selected: (id: string) => void;
  selectAll: () => void;
};

export const ApplicationList: React.FC<{
  isLoading: boolean;
  appls: Array<{
    application: ApplicationEntity;
    applicantSchemaKeys: ApplicantSchemaKeys;
  }>;
  selectable?: SelectableOption;
  selectedStatus: ApplyStatus[];
  setSelectedStatus: (status: ApplyStatus[]) => void;
  paging: {
    current: number;
    total: number;
  };
  openStatusUpdateModal: () => void;
  openBulkIssuePDFModal: () => void;
  openCsvUploadModal: () => void;
  openCsvDownloadModal: () => void;
  openBulkDownloadFilesModal: () => void;
}> = ({
  appls,
  isLoading,
  selectable,
  selectedStatus,
  setSelectedStatus,
  paging,
  openStatusUpdateModal,
  openBulkIssuePDFModal,
  openCsvUploadModal,
  openCsvDownloadModal,
  openBulkDownloadFilesModal,
}) => {
  const history = useHistory();
  const applicationIds = appls.map((a) => a.application.id);
  const isMultipleSelectable = !!selectable;
  const bulkCheckDropdown = useModal();
  const statusDropdown = useModal();

  const checked = React.useMemo(() => {
    return (
      paging.total > 0 &&
      selectable!.selectedApplicationIds &&
      applicationIds.every((v) =>
        selectable!.selectedApplicationIds.includes(v),
      )
    );
  }, [selectable, paging, applicationIds]);

  return (
    <>
      {selectable?.selectedProcedure && (
        <div className="application-list-selected">
          <div>
            <span className="has-text-weight-bold mr-1">
              {selectable!.selectedApplicationIds.length}
            </span>
            件の申請を選択中
          </div>
          <SecondaryButton
            isSmall
            text="対応ステータスを更新"
            onClick={openStatusUpdateModal}
            data-for-rpa-id={"update-status"}
            disabled={selectable!.selectedApplicationIds.length === 0}
          />
          <SecondaryButton
            isSmall
            text="CSVを出力"
            onClick={openCsvDownloadModal}
            disabled={selectable!.selectedApplicationIds.length === 0}
          />
          <SecondaryButton
            isSmall
            text="ファイルを交付"
            onClick={openBulkIssuePDFModal}
            disabled={
              selectable!.selectedApplicationIds.length === 0 ||
              !selectable.selectedProcedure.canOutputPdf()
            }
          />
          <SecondaryButton
            isSmall
            text="ダウンロード"
            data-for-rpa-id={"output-updated-files"}
            onClick={openBulkDownloadFilesModal}
            disabled={selectable!.selectedApplicationIds.length === 0}
          />
        </div>
      )}
      <Table wrapper={false} borderType="row">
        <TableHeader>
          <TableRow>
            {selectable?.selectedProcedure && isMultipleSelectable && (
              <TableCheckBoxTh
                checked={checked}
                onCheckAll={(e) => {
                  e.stopPropagation();
                  selectable!.selectAll();
                }}
              >
                {selectable?.selectedProcedure && (
                  <BulkApplicationSelectDropdown
                    isActive={bulkCheckDropdown.isActive}
                    onOpen={bulkCheckDropdown.open}
                    onClose={bulkCheckDropdown.close}
                    onOpenCsvUploadModal={openCsvUploadModal}
                  />
                )}
              </TableCheckBoxTh>
            )}
            <Th>申請番号</Th>
            <Th>
              <StatusFilterDropDown
                selectedStatus={selectedStatus}
                onSelect={setSelectedStatus}
                isActive={statusDropdown.isActive}
                onOpen={statusDropdown.open}
                onClose={statusDropdown.close}
              >
                対応ステータス
              </StatusFilterDropDown>
            </Th>
            <Th>手続き名称</Th>
            <Th>申請者名</Th>
            <Th>受付日時</Th>
          </TableRow>
        </TableHeader>
        <TableBody>
          <Loader isLoading={isLoading}>
            {appls.map(({ application: a, applicantSchemaKeys }) => (
              <TableRow
                key={a.id}
                onClick={() => {
                  history.push(
                    SmartApplyR.ApplicationDetail.link({ id: a.id }),
                  );
                }}
              >
                {selectable?.selectedProcedure && isMultipleSelectable && (
                  <TableCheckBoxTd
                    onClickCheckbox={(e) => {
                      e.stopPropagation();
                      selectable!.selected(a.id);
                    }}
                    checked={
                      selectable!.selectedApplicationIds &&
                      selectable!.selectedApplicationIds.indexOf(a.id) > -1
                    }
                    children={undefined}
                  />
                )}
                <Td whiteSpace="nowrap">
                  <Link
                    anchor={({ ...props }) => (
                      <RouterLink
                        {...props}
                        onClick={(e) => e.stopPropagation()}
                        to={SmartApplyR.ApplicationDetail.link({ id: a.id })}
                      />
                    )}
                  >
                    {a.formattedId}
                  </Link>
                </Td>
                <Td>
                  <div className="is-flex">
                    <ApplicationStatusTag status={a.status} />
                    <PaymentsStatusTag status={a.summarizedPaymentStatus()} />
                  </div>
                </Td>
                <Td>{a.name}</Td>
                <Td>{a.getApplicant(applicantSchemaKeys).name}</Td>
                <Td>{formatDatetime(a.createdAt)}</Td>
              </TableRow>
            ))}
          </Loader>
        </TableBody>
      </Table>
    </>
  );
};
